/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  McpServerUsageSubject,
  McpServerUsageSubject$inboundSchema,
} from "./mcpserverusagesubject.js";

/**
 * Active signed-in usage of an MCP server, for confirming a breaking change.
 */
export type McpServerUsageImpact = {
  /**
   * Unexpired, unrevoked sessions that can reach this server.
   */
  activeConnectionCount: number;
  /**
   * Distinct subjects (people, API keys, agents, workloads, anonymous callers) holding those sessions.
   */
  activeSubjectCount: number;
  /**
   * Most recent use across all those sessions, accurate to within five minutes. Absent when none has a recorded use.
   */
  lastUsedAt?: Date | undefined;
  /**
   * The server's display name, which confirmations ask the operator to type.
   */
  serverName: string;
  /**
   * Of active_connection_count, the sessions issued for the whole shared sign-in rather than this server's address. Always zero when shared_sign_in is false.
   */
  sharedConnectionCount: number;
  /**
   * Whether the server's user session issuer also serves other MCP servers or gateways. Connections not bound to this server's address may then be using one of those instead.
   */
  sharedSignIn: boolean;
  /**
   * Whether the server has a user session issuer. When false nothing is counted: callers are not signed in, so their usage is unknown.
   */
  signInRequired: boolean;
  /**
   * Subjects holding active sessions, most recently used first.
   */
  subjects: Array<McpServerUsageSubject>;
  /**
   * Whether more subjects exist than are listed.
   */
  subjectsTruncated: boolean;
};

/** @internal */
export const McpServerUsageImpact$inboundSchema: z.ZodMiniType<
  McpServerUsageImpact,
  unknown
> = z.pipe(
  z.object({
    active_connection_count: z.int(),
    active_subject_count: z.int(),
    last_used_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    server_name: z.string(),
    shared_connection_count: z.int(),
    shared_sign_in: z.boolean(),
    sign_in_required: z.boolean(),
    subjects: z.array(McpServerUsageSubject$inboundSchema),
    subjects_truncated: z.boolean(),
  }),
  z.transform((v) => {
    return remap$(v, {
      "active_connection_count": "activeConnectionCount",
      "active_subject_count": "activeSubjectCount",
      "last_used_at": "lastUsedAt",
      "server_name": "serverName",
      "shared_connection_count": "sharedConnectionCount",
      "shared_sign_in": "sharedSignIn",
      "sign_in_required": "signInRequired",
      "subjects_truncated": "subjectsTruncated",
    });
  }),
);

export function mcpServerUsageImpactFromJSON(
  jsonString: string,
): SafeParseResult<McpServerUsageImpact, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => McpServerUsageImpact$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'McpServerUsageImpact' from JSON`,
  );
}
